import {
    useListContext,
    Button,
    Confirm,
    useNotify,
    useUnselectAll,
    useResourceContext,
    useDeleteMany,
    useUpdateMany,
    useTranslate,
    useDataProvider,
    usePermissions,
    useLocaleState,
} from 'react-admin';
import { useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import DeactivateIcon from '@/assets/icons/deactivate-icon.svg?react';
import ActivateIcon from '@/assets/icons/activate-icon.svg?react';
import RestartIcon from '@/assets/icons/restart-icon.svg?react';
import DeleteIcon from '@/assets/icons/delete-icon.svg?react';
import ProductionDoneIcon from '@/assets/icons/production-done-device-icon.svg?react';
import ActiveIcon from '@/assets/icons/active-icon.svg?react';
import PropTypes from 'prop-types';
import { CustomConfirmContent } from '@/components/global/buttons/CustomConfirmContent';
import { globalStyles } from '@/themes/commonStyles/global.js';
import ExportIcon from '@/assets/icons/export-all-icon.svg?react';
import CloseIcon from '@/assets/icons/close-popup-icon.svg?react';
import exporter from '@/services/exporter.js';
import { useDeviceStates } from '@/constants/DeviceStates.jsx';
import { mapServiceRequestExportRow } from '@/components/service/mapServiceRequestExportRow.js';
import { mapServiceInspectionExportRow } from '@/components/service/mapServiceInspectionExportRow.js';
import { Palette } from '@/themes/themeOverrides/Palette';

export const BulkActionsContainer = ({ children, hideOnMobile = true }) => {
    const { selectedIds } = useListContext();
    const translate = useTranslate();
    const resource = useResourceContext();
    const unselectAll = useUnselectAll(resource);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    if (hideOnMobile && !isDesktop) {
        return null;
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }} className='bulk-actions-container'>
            {selectedIds.length ? (
                <Typography
                    variant='h6'
                    component='p'
                    sx={{
                        color: Palette.black,
                        fontSize: '14px',
                        fontWeight: '600',
                        lineHeight: '20px',
                        letterSpacing: '-0.14px',
                        fontFamily: 'Inter, Arial, sans-serif',
                    }}
                >
                    {translate('ra.action.bulk_actions_check')} {selectedIds.length} {translate('ra.action.bulk_actions_elements')}
                </Typography>
            ) : null}
            <Button label={translate('common.unselectAll')} onClick={unselectAll} startIcon={<CloseIcon />} disabled={!selectedIds.length} />
            {children}
        </Box>
    );
};

const ConfirmUpdateButton = ({ label, content, icon, data, resource }) => {
    const { selectedIds, refetch } = useListContext();
    const [open, setOpen] = useState(false);
    const notify = useNotify();
    const translate = useTranslate();
    const unselectAll = useUnselectAll(resource);

    data.ids = selectedIds;

    const [updateMany, { isLoading }] = useUpdateMany(
        resource,
        {
            ids: selectedIds,
            data,
        },
        {
            onSuccess: () => {
                notify(translate('common.operations.bulkUpdated'));
                unselectAll();
                refetch();
            },
            onError: error => {
                notify(error.message, { type: 'error' });
                refetch();
            },
        },
    );
    const handleClick = () => setOpen(true);
    const handleDialogClose = () => setOpen(false);
    const handleConfirm = () => {
        updateMany();
        setOpen(false);
    };

    return (
        <>
            <Button label={label} onClick={handleClick}>
                {icon}
            </Button>
            <Confirm
                isOpen={open}
                loading={isLoading}
                content={<CustomConfirmContent content={content} handleDialogClose={handleDialogClose} handleConfirm={handleConfirm} loading={isLoading} />}
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
                sx={{ ...globalStyles.customConfirmDialog }}
            />
        </>
    );
};

const ConfirmDeleteButton = ({ label, content, icon, data, resource }) => {
    const { selectedIds, refetch } = useListContext();
    const [open, setOpen] = useState(false);
    const notify = useNotify();
    const translate = useTranslate();
    const unselectAll = useUnselectAll(resource);

    data.ids = selectedIds;

    const [deleteMany, { isLoading }] = useDeleteMany(
        resource,
        {
            ids: selectedIds,
            data,
        },
        {
            onSuccess: () => {
                notify(translate('common.operations.bulkDeleted'));
                unselectAll();
                refetch();
            },
            onError: error => {
                notify(error.message, { type: 'error' });
                refetch();
            },
        },
    );
    const handleClick = () => setOpen(true);
    const handleDialogClose = () => setOpen(false);
    const handleConfirm = () => {
        deleteMany();
        setOpen(false);
    };

    return (
        <>
            <Button label={label} onClick={handleClick}>
                {icon}
            </Button>
            <Confirm
                isOpen={open}
                loading={isLoading}
                content={<CustomConfirmContent content={content} handleDialogClose={handleDialogClose} handleConfirm={handleConfirm} loading={isLoading} />}
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
                sx={{ ...globalStyles.customConfirmDialog }}
            />
        </>
    );
};

export const DefaultBulkActionButtons = ({ buttons, hasRestart = false, hasDelete = false, hasReturnToNew }) => {
    const resource = useResourceContext();
    const translate = useTranslate();
    const [locale] = useLocaleState();
    const { selectedIds } = useListContext();
    const isPolishLocale = locale?.toLowerCase().startsWith('pl');
    const translatedResource = isPolishLocale ? translate(`common.resourceNames.${resource}`, { _: resource }) : resource;
    return (
        <BulkActionsContainer>
            {buttons ? buttons.map(button => button) : null}
            <ConfirmUpdateButton
                label={translate('common.deviceActions.activate.label')}
                title={translate('common.deviceActions.activate.title')}
                content={translate('common.deviceActions.update', { resource: translatedResource })}
                icon={<ActivateIcon />}
                data={{ isActive: true }}
                resource={resource}
            />
            <ConfirmUpdateButton
                label={translate('common.deviceActions.deactivate.label')}
                title={translate('common.deviceActions.deactivate.title')}
                content={translate('common.deviceActions.update', { resource: translatedResource })}
                icon={<DeactivateIcon />}
                data={{ isActive: false }}
                resource={resource}
            />
            {hasRestart && (
                <ConfirmUpdateButton
                    label={translate('common.deviceActions.restart.label')}
                    title={translate('common.deviceActions.restart.title')}
                    content={translate('common.deviceActions.restartBulk', { resource: translatedResource })}
                    icon={<RestartIcon />}
                    data={{ isRestart: true }}
                    resource={resource}
                />
            )}
            {hasDelete && (
                <ConfirmDeleteButton
                    label={translate('common.remove')}
                    content={translate('ra.message.bulk_delete_content', { name: translatedResource, smart_count: selectedIds.length })}
                    icon={<DeleteIcon />}
                    data={{ isDeleted: true }}
                    resource={resource}
                />
            )}
            {hasReturnToNew && (
                <ConfirmUpdateButton
                    label={translate('common.deviceActions.changeToProductionDone')}
                    title={translate('common.deviceActions.changeToProductionDone')}
                    content={translate('common.deviceActions.changeToProductionDoneBulk', { resource: translatedResource })}
                    icon={<ProductionDoneIcon />}
                    data={{ deploymentStatus: 'productionDone' }}
                    resource={resource}
                />
            )}
        </BulkActionsContainer>
    );
};

const BulkDeviceActionButton = ({ label, content, icon, action, successMessage }) => {
    const { selectedIds, refetch } = useListContext();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const notify = useNotify();
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const resource = useResourceContext();
    const unselectAll = useUnselectAll(resource);

    const handleClick = () => setOpen(true);
    const handleDialogClose = () => setOpen(false);
    const handleConfirm = async () => {
        if (!selectedIds.length) {
            setOpen(false);
            return;
        }

        setLoading(true);
        try {
            const results = await Promise.allSettled(selectedIds.map(id => dataProvider.post(`device/${id}/${action}`)));
            const successCount = results.filter(result => result.status === 'fulfilled').length;
            const failedResults = results.filter(result => result.status === 'rejected');
            const failedCount = failedResults.length;
            if (failedCount === 0) {
                notify(successMessage);
            } else if (successCount === 0) {
                const firstError = failedResults[0]?.reason?.message;
                const errorSuffix = firstError ? `: ${firstError}` : '';
                notify(`${translate('common.errorOccurred')}${errorSuffix}`, { type: 'error' });
            } else {
                notify(`${successMessage} (${successCount}/${selectedIds.length}). ${failedCount} failed.`, { type: 'warning' });
            }

            unselectAll();
            refetch();
        } finally {
            setLoading(false);
            setOpen(false);
        }
    };

    return (
        <>
            <Button label={label} onClick={handleClick} disabled={!selectedIds.length}>
                {icon}
            </Button>
            <Confirm
                isOpen={open}
                loading={loading}
                content={<CustomConfirmContent content={content} handleDialogClose={handleDialogClose} handleConfirm={handleConfirm} loading={loading} />}
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
                sx={{ ...globalStyles.customConfirmDialog }}
            />
        </>
    );
};

export const DeviceIssueBulkActionButtons = () => {
    const translate = useTranslate();

    return (
        <BulkActionsContainer>
            <BulkDeviceActionButton
                label={translate('common.deviceActions.restart.label')}
                content={translate('common.deviceActions.restart.content')}
                icon={<RestartIcon />}
                action='restart'
                successMessage={translate('common.deviceActions.restart.success')}
            />
            <BulkDeviceActionButton
                label={translate('common.deviceActions.deactivate.label')}
                content={translate('common.deviceActions.deactivate.content')}
                icon={<DeactivateIcon />}
                action='deactivate'
                successMessage={translate('common.deviceActions.deactivate.success')}
            />
            <BulkDeviceActionButton
                label={translate('common.deviceActions.fixProblem.problemSolved')}
                content={translate('common.deviceActions.fixProblem.content')}
                icon={<ActiveIcon />}
                action='fix-problem'
                successMessage={translate('common.deviceActions.fixProblem.problemSolved')}
            />
        </BulkActionsContainer>
    );
};

export const OnlyBulkDeleteActionButton = ({ buttons }) => {
    const resource = useResourceContext();
    const { selectedIds } = useListContext();
    const translate = useTranslate();
    const [locale] = useLocaleState();
    const isPolishLocale = locale?.toLowerCase().startsWith('pl');
    const translatedResource = isPolishLocale ? translate(`common.resourceNames.${resource}`, { _: resource }) : resource;
    return (
        <BulkActionsContainer>
            {buttons ? buttons.map(button => button) : null}
            <ConfirmDeleteButton
                label={translate('common.remove')}
                content={translate('ra.message.bulk_delete_content', { name: translatedResource, smart_count: selectedIds.length })}
                icon={<DeleteIcon />}
                data={{ isDeleted: true }}
                resource={resource}
            />
        </BulkActionsContainer>
    );
};

export const ServiceBulkActionButtons = ({ buttons }) => {
    const { permissions } = usePermissions();
    const resource = useResourceContext();
    const translate = useTranslate();
    const { selectedIds } = useListContext();
    const dataProvider = useDataProvider();
    const deviceStates = useDeviceStates();
    const notify = useNotify();
    const [isExporting, setIsExporting] = useState(false);
    const isServiceInspection = resource === 'service-inspection';

    const handleExport = async () => {
        if (!selectedIds.length || isExporting) return;
        setIsExporting(true);

        try {
            const { data } = await dataProvider.getMany(resource, { ids: selectedIds });
            const rows = data.map(record =>
                isServiceInspection
                    ? mapServiceInspectionExportRow(record, translate, deviceStates)
                    : mapServiceRequestExportRow(record, translate, deviceStates),
            );
            exporter(rows, null, null, resource);
        } catch (error) {
            notify(error.message, { type: 'error' });
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <BulkActionsContainer>
            {(permissions.isAdmin || permissions.isService) && <>{buttons ? buttons.map(button => button) : null}</>}
            <Button
                label={translate('common.exportSelected')}
                onClick={handleExport}
                startIcon={<ExportIcon />}
                disabled={!selectedIds.length || isExporting}
            />
        </BulkActionsContainer>
    );
};

BulkActionsContainer.propTypes = {
    children: PropTypes.node,
    hideOnMobile: PropTypes.bool,
};

DefaultBulkActionButtons.propTypes = {
    buttons: PropTypes.arrayOf(PropTypes.node),
    hasRestart: PropTypes.bool,
    hasDelete: PropTypes.bool,
};

OnlyBulkDeleteActionButton.propTypes = {
    buttons: PropTypes.arrayOf(PropTypes.node),
};

BulkDeviceActionButton.propTypes = {
    label: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
    action: PropTypes.string.isRequired,
    successMessage: PropTypes.string.isRequired,
};

ServiceBulkActionButtons.propTypes = {
    buttons: PropTypes.arrayOf(PropTypes.node),
};

ConfirmUpdateButton.propTypes = {
    label: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
    data: PropTypes.object.isRequired,
    resource: PropTypes.string.isRequired,
};

ConfirmDeleteButton.propTypes = {
    label: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
    data: PropTypes.object.isRequired,
    resource: PropTypes.string.isRequired,
};
